import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Building2, MapPin, Home, DollarSign, Hash, Layers } from 'lucide-react';

/**
 * Property form with building selector (create-new-building inline).
 * Backward-compatible props: { isOpen, onClose, onSubmit }
 * Optional prop: presetBuildingId — auto-selects a building on open.
 */
const PropertyForm = ({ isOpen, onClose, onSubmit, presetBuildingId = null }) => {
  const [buildings, setBuildings] = useState([]);
  const [formData, setFormData] = useState({
    name: '', building_id: '', house_number: '',
    address: '', city: '', zip_code: '',
    property_type: 'apartment',
    bedrooms: '', bathrooms: '', square_feet: '',
    rent_amount: '', deposit_amount: '',
    description: '',
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showNewBuilding, setShowNewBuilding] = useState(false);

  // Load buildings when the modal opens
  useEffect(() => {
    if (!isOpen) return;
    axios.get('/api/buildings').then(r => setBuildings(r.data.buildings || [])).catch(() => {});
    if (presetBuildingId) setFormData(f => ({ ...f, building_id: String(presetBuildingId) }));
  }, [isOpen, presetBuildingId]);

  // Prefill address from selected building
  useEffect(() => {
    const b = buildings.find(x => String(x.id) === String(formData.building_id));
    if (b) {
      setFormData(f => ({
        ...f,
        address: f.address || b.address || '',
        city: f.city || b.city || '',
        zip_code: f.zip_code || b.zip_code || '',
      }));
    }
  }, [formData.building_id, buildings]);

  const propertyTypes = [
    { value: 'apartment',  label: 'Apartment / Flat' },
    { value: 'house',      label: 'House' },
    { value: 'condo',      label: 'Condo' },
    { value: 'commercial', label: 'Commercial' },
    { value: 'land',       label: 'Land' },
  ];
  const kenyanCities = ['Nairobi','Mombasa','Kisumu','Nakuru','Eldoret','Thika','Malindi','Kitale','Kakamega','Nyeri'];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const err = {};
    if (!formData.name.trim())     err.name    = 'Property name is required';
    if (!formData.address.trim())  err.address = 'Address is required';
    if (!formData.city.trim())     err.city    = 'City is required';
    if (!formData.zip_code.trim()) err.zip_code = 'ZIP code is required';
    if (!formData.rent_amount || parseFloat(formData.rent_amount) <= 0) err.rent_amount = 'Valid rent amount is required';
    if (formData.zip_code.trim() && !/^\d{5}$/.test(formData.zip_code.trim())) err.zip_code = 'ZIP code must be 5 digits';
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      const b = buildings.find(x => String(x.id) === String(formData.building_id));
      const submitData = {
        ...formData,
        bedrooms:       formData.bedrooms       ? parseInt(formData.bedrooms) : null,
        bathrooms:      formData.bathrooms      ? parseFloat(formData.bathrooms) : null,
        square_feet:    formData.square_feet    ? parseFloat(formData.square_feet) : null,
        rent_amount:    parseFloat(formData.rent_amount),
        deposit_amount: formData.deposit_amount ? parseFloat(formData.deposit_amount) : 0,
        house_number:   formData.house_number || null,
        building_id:    formData.building_id ? parseInt(formData.building_id) : null,
        building_name:  b ? b.name : null,
      };
      await onSubmit(submitData);
      handleClose();
    } catch (err) {
      // parent shows toast
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '', building_id: '', house_number: '',
      address: '', city: '', zip_code: '',
      property_type: 'apartment',
      bedrooms: '', bathrooms: '', square_feet: '',
      rent_amount: '', deposit_amount: '', description: '',
    });
    setErrors({});
    setIsSubmitting(false);
    setShowNewBuilding(false);
    onClose();
  };

  useEffect(() => {
    const onEsc = (e) => { if (e.key === 'Escape' && isOpen) handleClose(); };
    if (isOpen) {
      document.addEventListener('keydown', onEsc);
      return () => document.removeEventListener('keydown', onEsc);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-neutral-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in" onClick={handleClose}>
      <div className="bg-white rounded-xl shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-slide-up" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-neutral-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-primary-50 text-primary-700 flex items-center justify-center">
              <Building2 className="h-4 w-4" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-neutral-900">New property</h2>
              <p className="text-xs text-neutral-500">Standalone unit or part of a building</p>
            </div>
          </div>
          <button onClick={handleClose} className="p-2 hover:bg-neutral-100 rounded-md text-neutral-400 hover:text-neutral-700">
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-5">
          {/* Building */}
          <section>
            <h3 className="text-xs uppercase tracking-wide text-neutral-500 font-semibold mb-2 flex items-center gap-1.5">
              <Layers className="h-3.5 w-3.5" /> Building (optional)
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <select name="building_id" value={formData.building_id} onChange={handleChange} className="input">
                  <option value="">— Standalone unit —</option>
                  {buildings.map(b => (
                    <option key={b.id} value={b.id}>
                      {b.name} · {b.city} ({b.units_count || 0} units)
                    </option>
                  ))}
                </select>
                {!showNewBuilding && (
                    <button type="button" onClick={() => setShowNewBuilding(true)}
                            className="mt-2 text-xs text-primary-700 hover:text-primary-800 font-medium">
                        + Create a new building
                    </button>
                )}
                {showNewBuilding && (
                    <NewBuildingInline onCreated={b => {
                        setBuildings(prev => [...prev, b]);
                        setFormData(f => ({ ...f, building_id: String(b.id) }));
                        setShowNewBuilding(false);
                    }} onCancel={() => setShowNewBuilding(false)} />
                )}
              </div>
            </div>
          </section>

          {/* Basics */}
          <section>
            <h3 className="text-xs uppercase tracking-wide text-neutral-500 font-semibold mb-2 flex items-center gap-1.5">
              <Home className="h-3.5 w-3.5" /> Unit
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-neutral-700 mb-1">Unit / property name *</label>
                <input type="text" name="name" value={formData.name} onChange={handleChange}
                       className={`input ${errors.name ? 'border-danger-300' : ''}`}
                       placeholder="e.g. Unit A3" />
                {errors.name && <p className="text-xs text-danger-600 mt-1">{errors.name}</p>}
              </div>
              <div>
                <label className="block text-xs font-medium text-neutral-700 mb-1">House / unit number</label>
                <input type="text" name="house_number" value={formData.house_number} onChange={handleChange}
                       className="input" placeholder="e.g. A3, 12" />
              </div>
              <div>
                <label className="block text-xs font-medium text-neutral-700 mb-1">Type *</label>
                <select name="property_type" value={formData.property_type} onChange={handleChange} className="input">
                  {propertyTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
            </div>
          </section>

          {/* Address */}
          <section>
            <h3 className="text-xs uppercase tracking-wide text-neutral-500 font-semibold mb-2 flex items-center gap-1.5">
              <MapPin className="h-3.5 w-3.5" /> Address
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="block text-xs font-medium text-neutral-700 mb-1">Street address *</label>
                <input type="text" name="address" value={formData.address} onChange={handleChange}
                       className={`input ${errors.address ? 'border-danger-300' : ''}`} placeholder="123 Ngong Rd" />
                {errors.address && <p className="text-xs text-danger-600 mt-1">{errors.address}</p>}
              </div>
              <div>
                <label className="block text-xs font-medium text-neutral-700 mb-1">City *</label>
                <select name="city" value={formData.city} onChange={handleChange}
                        className={`input ${errors.city ? 'border-danger-300' : ''}`}>
                    <option value="">Select…</option>
                    {kenyanCities.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                {errors.city && <p className="text-xs text-danger-600 mt-1">{errors.city}</p>}
              </div>
              <div>
                <label className="block text-xs font-medium text-neutral-700 mb-1">ZIP *</label>
                <input type="text" name="zip_code" value={formData.zip_code} onChange={handleChange} maxLength="5"
                       className={`input ${errors.zip_code ? 'border-danger-300' : ''}`} placeholder="00100" />
                {errors.zip_code && <p className="text-xs text-danger-600 mt-1">{errors.zip_code}</p>}
              </div>
            </div>
          </section>

          {/* Details */}
          <section>
            <h3 className="text-xs uppercase tracking-wide text-neutral-500 font-semibold mb-2">Details</h3>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-neutral-700 mb-1">Bedrooms</label>
                <input type="number" name="bedrooms" value={formData.bedrooms} onChange={handleChange} min="0" className="input" placeholder="2" />
              </div>
              <div>
                <label className="block text-xs font-medium text-neutral-700 mb-1">Bathrooms</label>
                <input type="number" name="bathrooms" value={formData.bathrooms} onChange={handleChange} min="0" step="0.5" className="input" placeholder="1.5" />
              </div>
              <div>
                <label className="block text-xs font-medium text-neutral-700 mb-1">Sq ft</label>
                <input type="number" name="square_feet" value={formData.square_feet} onChange={handleChange} min="0" className="input" placeholder="1200" />
              </div>
            </div>
          </section>

          {/* Financial */}
          <section>
            <h3 className="text-xs uppercase tracking-wide text-neutral-500 font-semibold mb-2 flex items-center gap-1.5">
              <DollarSign className="h-3.5 w-3.5" /> Financials
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-neutral-700 mb-1">Monthly rent (KES) *</label>
                <input type="number" name="rent_amount" value={formData.rent_amount} onChange={handleChange} min="0" step="0.01"
                       className={`input ${errors.rent_amount ? 'border-danger-300' : ''}`} placeholder="50000" />
                {errors.rent_amount && <p className="text-xs text-danger-600 mt-1">{errors.rent_amount}</p>}
              </div>
              <div>
                <label className="block text-xs font-medium text-neutral-700 mb-1">Deposit (KES)</label>
                <input type="number" name="deposit_amount" value={formData.deposit_amount} onChange={handleChange} min="0" step="0.01" className="input" placeholder="50000" />
              </div>
            </div>
          </section>

          <div>
            <label className="block text-xs font-medium text-neutral-700 mb-1">Description</label>
            <textarea name="description" value={formData.description} onChange={handleChange} rows="2" className="input" placeholder="Additional notes…" />
          </div>

          <div className="flex justify-end gap-2 pt-3 border-t border-neutral-100">
            <button type="button" onClick={handleClose} className="btn-secondary">Cancel</button>
            <button type="submit" disabled={isSubmitting} className="btn-primary">
              {isSubmitting ? 'Adding…' : 'Add property'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const NewBuildingInline = ({ onCreated, onCancel }) => {
    const [f, setF] = useState({ name:'', address:'', city:'', zip_code:'', building_type:'apartment_block' });
    const [saving, setSaving] = useState(false);
    const set = (k,v) => setF(x => ({ ...x, [k]: v }));
    const submit = async e => {
        e.preventDefault();
        if (!f.name || !f.address || !f.city) return;
        setSaving(true);
        try {
            const { data } = await axios.post('/api/buildings', f);
            onCreated(data.building || { id: data.id, ...f });
        } catch { setSaving(false); }
    };
    return (
        <div className="mt-3 p-3 border border-primary-200 bg-primary-50/30 rounded-lg">
            <div className="text-xs font-semibold text-primary-800 mb-2">Create new building</div>
            <div className="grid grid-cols-2 gap-2">
                <input required placeholder="Building name" value={f.name} onChange={e => set('name', e.target.value)} className="input" />
                <select value={f.building_type} onChange={e => set('building_type', e.target.value)} className="input">
                    <option value="apartment_block">Apartment block</option>
                    <option value="townhouses">Townhouses</option>
                    <option value="commercial">Commercial</option>
                    <option value="mixed_use">Mixed use</option>
                </select>
                <input required placeholder="Address" value={f.address} onChange={e => set('address', e.target.value)} className="input col-span-2" />
                <input required placeholder="City" value={f.city} onChange={e => set('city', e.target.value)} className="input" />
                <input placeholder="ZIP" value={f.zip_code} onChange={e => set('zip_code', e.target.value)} className="input" />
            </div>
            <div className="mt-2 flex justify-end gap-2">
                <button type="button" onClick={onCancel} className="btn-secondary btn-sm">Cancel</button>
                <button type="button" onClick={submit} disabled={saving} className="btn-primary btn-sm">{saving ? 'Creating…' : 'Create & select'}</button>
            </div>
        </div>
    );
};

export default PropertyForm;
